import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import Button from "../../atoms/Button";
import AccountDeletionPublicLayout from "./PublicLayout";
import AccountDeletionBillingImpactNotice from "../../molecules/AccountDeletionBillingImpactNotice";
import {
    accountDeletionService,
    createAccountDeletionIdempotencyKey,
} from "../../../services/accountDeletionService";
import {
    clearAccountDeletionSsoCallback,
    rememberAccountDeletionSsoCallback,
    navigateToAccountDeletionSsoProvider,
    requireSafeAccountDeletionAuthorizeUrl,
} from "../../../services/accountDeletionSsoCallback";
import {
    getAccountDeletionActionGrant,
    getAccountDeletionChallengeId,
} from "../../../services/accountDeletionResponseValidation";
import { submitAccountDeletion } from "../../../services/accountDeletionSubmission";
import { useAccountDeletionAnonymousSession } from "../../../hooks/useAccountDeletionAnonymousSession";
import { useSensitivePublicPage } from "../../../hooks/useSensitivePublicPage";
import { useAccountDeletionSsoProviders } from "../../../hooks/useAccountDeletionSsoProviders";
import { useClearSensitiveRefsOnPageHide } from "../../../hooks/useClearSensitiveRefsOnPageHide";

const CONFIRMATION = "DELETE";
const START_STEP = "start";
const CHALLENGE_STEP = "challenge";
const FINAL_STEP = "final";

const AccountDeletionRestrictedPage = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    useSensitivePublicPage(t("accountDeletionPublic.restricted.documentTitle"));
    const isPublicSessionReady = useAccountDeletionAnonymousSession();
    const ssoProviders = useAccountDeletionSsoProviders(isPublicSessionReady);
    const [step, setStep] = useState(START_STEP);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [typedConfirmation, setTypedConfirmation] = useState("");
    const [code, setCode] = useState("");
    const [challengeId, setChallengeId] = useState(null);
    const [genericStartAccepted, setGenericStartAccepted] = useState(false);
    const [errorKey, setErrorKey] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const reauthTokenRef = useRef(null);
    const idempotencyKeyRef = useRef(null);
    useClearSensitiveRefsOnPageHide(reauthTokenRef, idempotencyKeyRef);
    const emailInputRef = useRef(null);
    const codeInputRef = useRef(null);
    const finalRegionRef = useRef(null);

    useEffect(() => {
        if (!isPublicSessionReady) {
            return;
        }
        const focusTarget = step === START_STEP
            ? emailInputRef.current
            : step === CHALLENGE_STEP
                ? codeInputRef.current
                : finalRegionRef.current;
        try {
            focusTarget?.focus({ preventScroll: true });
        } catch (_) {
            focusTarget?.focus();
        }
    }, [isPublicSessionReady, step]);

    const resetAuthorization = () => {
        reauthTokenRef.current = null;
        idempotencyKeyRef.current = null;
        setChallengeId(null);
        setCode("");
        setStep(START_STEP);
    };

    const startPasswordAuthorization = async (event) => {
        event.preventDefault();
        if (!isPublicSessionReady
            || !email.trim()
            || !password
            || typedConfirmation !== CONFIRMATION) {
            setErrorKey("accountDeletionPublic.restricted.errors.proofRequired");
            return;
        }
        setIsSubmitting(true);
        setErrorKey(null);
        try {
            const response = await accountDeletionService.startRestrictedAuthorization({
                email,
                password,
                confirmation: CONFIRMATION,
            });
            const nextChallengeId = getAccountDeletionChallengeId(response);
            setGenericStartAccepted(true);
            setEmail("");
            setPassword("");
            setTypedConfirmation("");
            if (nextChallengeId) {
                setChallengeId(nextChallengeId);
                setStep(CHALLENGE_STEP);
            }
        } catch (_) {
            setErrorKey("accountDeletionPublic.restricted.errors.generic");
        } finally {
            setPassword("");
            setIsSubmitting(false);
        }
    };

    const startSsoAuthorization = async (provider) => {
        if (!isPublicSessionReady
            || !email.trim()
            || typedConfirmation !== CONFIRMATION
            || !ssoProviders.includes(provider)) {
            setErrorKey("accountDeletionPublic.restricted.errors.ssoProofRequired");
            return;
        }
        setIsSubmitting(true);
        setErrorKey(null);
        clearAccountDeletionSsoCallback();
        try {
            const response = await accountDeletionService.startRestrictedSsoAuthorization({
                email,
                provider,
                confirmation: CONFIRMATION,
            });
            const authorizeUrl = requireSafeAccountDeletionAuthorizeUrl(response?.authorizeUrl);
            rememberAccountDeletionSsoCallback({ flow: "restricted", provider });
            setEmail("");
            setPassword("");
            setTypedConfirmation("");
            navigateToAccountDeletionSsoProvider(authorizeUrl);
        } catch (_) {
            clearAccountDeletionSsoCallback();
            setErrorKey("accountDeletionPublic.restricted.errors.generic");
            setIsSubmitting(false);
        }
    };

    const completeChallenge = async (event) => {
        event.preventDefault();
        if (!challengeId || !/^\d{6}$/.test(code)) {
            setErrorKey("accountDeletionPublic.restricted.errors.codeRequired");
            return;
        }
        setIsSubmitting(true);
        setErrorKey(null);
        try {
            const response = await accountDeletionService.completeRestrictedAuthorization({
                challengeId,
                code,
                confirmation: CONFIRMATION,
            });
            const reauthToken = getAccountDeletionActionGrant(response);
            if (!reauthToken) {
                throw new TypeError("Restricted account-deletion grant is unavailable");
            }
            reauthTokenRef.current = reauthToken;
            idempotencyKeyRef.current = createAccountDeletionIdempotencyKey();
            setChallengeId(null);
            setCode("");
            setStep(FINAL_STEP);
        } catch (_) {
            setErrorKey("accountDeletionPublic.restricted.errors.generic");
        } finally {
            setCode("");
            setIsSubmitting(false);
        }
    };

    const createDeletion = async () => {
        if (!reauthTokenRef.current || !idempotencyKeyRef.current) {
            setErrorKey("accountDeletionPublic.restricted.errors.expired");
            resetAuthorization();
            return;
        }
        setIsSubmitting(true);
        setErrorKey(null);
        try {
            await submitAccountDeletion({
                reauthToken: reauthTokenRef.current,
                idempotencyKey: idempotencyKeyRef.current,
                dispatch,
            });
            reauthTokenRef.current = null;
            idempotencyKeyRef.current = null;
        } catch (error) {
            setErrorKey("accountDeletionPublic.restricted.errors.generic");
            if (error?.code === "REAUTHENTICATION_REQUIRED"
                || error?.code === "IDEMPOTENCY_CONFLICT") {
                resetAuthorization();
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const isBusy = isSubmitting || !isPublicSessionReady;

    return (
        <AccountDeletionPublicLayout
            title={t("accountDeletionPublic.restricted.title")}
            description={t("accountDeletionPublic.restricted.description")}
            showRecoveryLink
        >
            {errorKey && (
                <div className="account-deletion-public-card__feedback account-deletion-public-card__feedback--error" role="alert">
                    {t(errorKey)}
                </div>
            )}

            {!isPublicSessionReady && (
                <div className="account-deletion-public-card__feedback" role="status" aria-live="polite">
                    {t("accountDeletionPublic.preparing")}
                </div>
            )}

            {genericStartAccepted && step === START_STEP && (
                <div className="account-deletion-public-card__feedback" role="status" aria-live="polite">
                    {t("accountDeletionPublic.restricted.genericStartAccepted")}
                </div>
            )}

            {step === START_STEP ? (
                <form className="account-deletion-public-card__form" onSubmit={startPasswordAuthorization} noValidate>
                    <label htmlFor="account-deletion-restricted-email">
                        {t("accountDeletionPublic.restricted.fields.email")}
                        <input
                            ref={emailInputRef}
                            id="account-deletion-restricted-email"
                            type="email"
                            value={email}
                            autoComplete="username"
                            onChange={(event) => setEmail(event.target.value)}
                            disabled={isBusy}
                        />
                    </label>
                    <label htmlFor="account-deletion-restricted-password">
                        {t("accountDeletionPublic.restricted.fields.password")}
                        <input
                            id="account-deletion-restricted-password"
                            type="password"
                            value={password}
                            autoComplete="current-password"
                            onChange={(event) => setPassword(event.target.value)}
                            disabled={isBusy}
                        />
                    </label>
                    <label htmlFor="account-deletion-restricted-confirmation">
                        {t("accountDeletionPublic.restricted.fields.confirmation")}
                        <input
                            id="account-deletion-restricted-confirmation"
                            value={typedConfirmation}
                            autoComplete="off"
                            spellCheck="false"
                            onChange={(event) => setTypedConfirmation(event.target.value)}
                            disabled={isBusy}
                        />
                    </label>
                    <div className="account-deletion-public-card__actions">
                        <Button type="submit" variant="danger" disabled={isBusy}>
                            {isSubmitting
                                ? t("accountDeletionPublic.restricted.actions.starting")
                                : t("accountDeletionPublic.restricted.actions.start")}
                        </Button>
                        {ssoProviders.map((provider) => (
                            <Button
                                key={provider}
                                type="button"
                                variant="secondary"
                                disabled={isBusy}
                                onClick={() => startSsoAuthorization(provider)}
                            >
                                {t(`accountDeletionPublic.restricted.actions.${provider}`)}
                            </Button>
                        ))}
                    </div>
                </form>
            ) : step === CHALLENGE_STEP ? (
                <form className="account-deletion-public-card__form" onSubmit={completeChallenge} noValidate>
                    <p>{t("accountDeletionPublic.restricted.challengeInstructions")}</p>
                    <label htmlFor="account-deletion-restricted-code">
                        {t("accountDeletionPublic.restricted.fields.code")}
                        <input
                            ref={codeInputRef}
                            id="account-deletion-restricted-code"
                            value={code}
                            inputMode="numeric"
                            pattern="[0-9]{6}"
                            maxLength={6}
                            autoComplete="one-time-code"
                            onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                            disabled={isBusy}
                        />
                    </label>
                    <div className="account-deletion-public-card__actions">
                        <Button type="submit" variant="danger" disabled={isBusy}>
                            {isSubmitting
                                ? t("accountDeletionPublic.restricted.actions.verifying")
                                : t("accountDeletionPublic.restricted.actions.verify")}
                        </Button>
                    </div>
                </form>
            ) : (
                <div
                    ref={finalRegionRef}
                    className="account-deletion-public-card__status"
                    role="status"
                    aria-live="polite"
                    tabIndex={-1}
                >
                    <p>{t("accountDeletionPublic.restricted.finalWarning")}</p>
                    <AccountDeletionBillingImpactNotice nonEnumerating />
                    <Button variant="danger" onClick={createDeletion} disabled={isBusy}>
                        {isSubmitting
                            ? t("accountDeletionPublic.restricted.actions.deleting")
                            : t("accountDeletionPublic.restricted.actions.delete")}
                    </Button>
                </div>
            )}
        </AccountDeletionPublicLayout>
    );
};

export default AccountDeletionRestrictedPage;
